/**
 * Monthly Giving Summary adapter.
 *
 * The pure aggregation (month windowing with the inlined local-date parser, void
 * exclusion, per-fund rollup, gift de-dup, totals) lives in
 * @glorify-llc/accounting-core and is shared with Relay (ADR-006). This file
 * keeps the Forge-specific seam: it injects the metric-registry summary labels.
 *
 * Consumers keep importing from "@/lib/reports/adapters/monthly-giving-summary".
 */
import { type ReportAggregation } from "@glorify-llc/accounting-core";
import { buildReportSummaryItem } from "@/lib/metrics/metric-registry";
import {
  aggregateMonthlyGivingSummary,
  type MonthlyGivingSummaryContext as CoreMonthlyGivingSummaryContext,
  type MonthlyContribution,
} from "@glorify-llc/accounting-core";

export type MonthlyGivingSummaryContext = CoreMonthlyGivingSummaryContext & {
  periodStart?: string | Date;
  periodEnd?: string | Date;
};

export type {
  MonthlyContribution,
  MonthlyAllocation,
} from "@glorify-llc/accounting-core";

const VOID_STATUSES = new Set([
  "void",
  "voided",
  "refunded",
  "reversed",
  "failed",
  "canceled",
  "cancelled",
]);

const parseContributionDate = (raw: string | Date): Date | null => {
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toFiniteNumber = (value: unknown): number => {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) ? number : 0;
};

const roundCents = (value: number): number => Math.round(value * 100) / 100;

const formatLocalDateParam = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildContributionDrilldownUrl = (
  fundId: string | undefined,
  periodStart?: Date,
  periodEnd?: Date,
): string => {
  const params = new URLSearchParams();
  if (fundId) params.set("fund", fundId);
  if (periodStart && periodEnd) {
    params.set("range", "custom");
    params.set("rangeStart", formatLocalDateParam(periodStart));
    params.set("rangeEnd", formatLocalDateParam(periodEnd));
  }
  const query = params.toString();
  return query ? `/contributions?${query}` : "/contributions";
};

/**
 * The published accounting-core package's v1 contract only accepts `asOf`
 * and therefore always clips to that calendar month. Until the shared package
 * publishes a range-aware contract, keep the Forge seam honest by aggregating
 * the selected inclusive period here.
 */
function aggregateSelectedPeriod(
  contributions: MonthlyContribution[],
  periodStart: string | Date,
  periodEnd: string | Date,
  asOf: Date,
) {
  const start = parseContributionDate(periodStart);
  const end = parseContributionDate(periodEnd);
  const cutoff = parseContributionDate(asOf);
  if (!start || !end || !cutoff || start > end) {
    return { rows: [], fundCount: 0, totalAmount: 0 };
  }
  const effectiveEnd = cutoff < end ? cutoff : end;

  const totals = new Map<
    string,
    {
      fundId?: string;
      fundName: string;
      total: number;
      donorIds: Set<string>;
      contributionIds: Set<string>;
    }
  >();

  for (const contribution of contributions) {
    if (VOID_STATUSES.has((contribution.status ?? "").toLowerCase())) continue;
    const date = parseContributionDate(contribution.date);
    if (!date || date < start || date > effectiveEnd) continue;

    const allocations = contribution.allocations?.length
      ? contribution.allocations
      : [{ amount: contribution.amount, fund: { id: undefined, name: "Unassigned" } }];

    for (const allocation of allocations) {
      const fundId = allocation.fund?.id ?? undefined;
      const fundName = allocation.fund?.name || "Unassigned";
      const key = fundId || fundName;
      const current = totals.get(key) ?? {
        fundId,
        fundName,
        total: 0,
        donorIds: new Set<string>(),
        contributionIds: new Set<string>(),
      };
      current.total += toFiniteNumber(allocation.amount);
      if (contribution.person_id) current.donorIds.add(contribution.person_id);
      current.contributionIds.add(contribution.id);
      totals.set(key, current);
    }
  }

  const rows = Array.from(totals.values()).map((fund) => ({
    id: fund.fundId || fund.fundName,
    label: fund.fundName,
    fund: fund.fundName,
    donors: fund.donorIds.size,
    gifts: fund.contributionIds.size,
    totalAmount: roundCents(fund.total),
    entityType: "contribution" as const,
    drilldownUrl: buildContributionDrilldownUrl(fund.fundId, start, effectiveEnd),
  }));

  return {
    rows,
    fundCount: rows.length,
    totalAmount: roundCents(rows.reduce((sum, row) => sum + row.totalAmount, 0)),
  };
}

export function buildMonthlyGivingSummaryAggregation(
  context: MonthlyGivingSummaryContext,
): ReportAggregation {
  const { rows, fundCount, totalAmount } =
    context.periodStart && context.periodEnd
      ? aggregateSelectedPeriod(
          context.contributions,
          context.periodStart,
          context.periodEnd,
          context.asOf,
        )
      : aggregateMonthlyGivingSummary(context);

  return {
    asOf: context.asOf,
    summary: [
      buildReportSummaryItem("report.monthly_giving.fund_count", fundCount),
      buildReportSummaryItem("report.monthly_giving.total_amount", totalAmount),
    ],
    rows,
  };
}
